import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { useCreateGroup, useDeleteGroup, useGroups, useUpdateGroup } from "../../api/queries";
import type { Group } from "../../api/types";
import { AbilityGate, granted } from "../../auth/AbilityGate";
import { useAuth } from "../../auth/auth-context";
import { Button } from "../../ui/Button";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { Dialog } from "../../ui/Dialog";
import { TextField } from "../../ui/Field";
import { Menu, type MenuItem } from "../../ui/Menu";
import { SettingsPage } from "../../ui/SettingsSection";
import { useToast } from "../../ui/toast-context";

interface GroupDraft {
  id?: string;
  name: string;
  description: string;
}

/** Create and edit share one dialog; an `id` in the draft is what tells them apart. */
function GroupDialog({
  draft,
  onClose,
}: {
  draft: GroupDraft | null;
  onClose: () => void;
}): ReactNode {
  const [name, setName] = useState(draft?.name ?? "");
  const [description, setDescription] = useState(draft?.description ?? "");
  const [error, setError] = useState<string | undefined>(undefined);
  const create = useCreateGroup();
  const update = useUpdateGroup();
  const toast = useToast();
  const editing = draft?.id !== undefined;
  const pending = create.isPending || update.isPending;

  const submit = (): void => {
    const trimmed = name.trim();
    if (trimmed === "") {
      setError("A group needs a name");
      return;
    }
    setError(undefined);
    const body = { name: trimmed, description: description.trim() };
    const options = {
      onSuccess: () => {
        toast.show(editing ? "Group updated." : `Group “${trimmed}” created.`);
        onClose();
      },
      onError: (cause: Error) => {
        toast.show(cause.message, "error");
      },
    };
    if (draft?.id === undefined) {
      create.mutate(body, options);
    } else {
      update.mutate({ id: draft.id, ...body }, options);
    }
  };

  return (
    <Dialog
      open={draft !== null}
      onClose={onClose}
      title={editing ? "Edit group" : "New group"}
      description="Groups are granted access to collections and documents as a unit."
    >
      <div className="flex flex-col gap-4">
        <TextField label="Name" value={name} onChange={setName} error={error} />
        <TextField
          label="Description"
          description="Optional. What this group is for."
          value={description}
          onChange={setDescription}
        />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={pending} onClick={submit}>
          {editing ? "Save changes" : "Create group"}
        </Button>
      </div>
    </Dialog>
  );
}

export function GroupsScreen(): ReactNode {
  const { session } = useAuth();
  const groups = useGroups();
  const remove = useDeleteGroup();
  const toast = useToast();
  const [draft, setDraft] = useState<GroupDraft | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Group | null>(null);

  const abilities = groups.data?.abilities ?? {};
  const rows = groups.data?.groups ?? [];

  const menuItems = (group: Group): MenuItem[] => {
    const can = abilities[group.id] ?? {};
    const items: MenuItem[] = [];
    if (granted(can, "update")) {
      items.push({
        label: "Edit…",
        onSelect: () => {
          setDraft({ id: group.id, name: group.name, description: group.description ?? "" });
        },
      });
    }
    if (granted(can, "delete")) {
      items.push({
        label: "Delete…",
        destructive: true,
        onSelect: () => {
          setPendingDelete(group);
        },
      });
    }
    return items;
  };

  return (
    <SettingsPage
      title="Groups"
      description="A group is a set of people you can grant access with in one move."
      actions={
        <AbilityGate
          id={session?.info.team.id}
          ability="createGroup"
          fallback={
            <span className="text-xs text-muted">Only an administrator may add groups.</span>
          }
        >
          <Button
            variant="primary"
            onClick={() => {
              setDraft({ name: "", description: "" });
            }}
          >
            New group
          </Button>
        </AbilityGate>
      }
    >
      {groups.isPending ? (
        <p role="status" className="text-sm text-muted">
          Loading groups…
        </p>
      ) : groups.isError ? (
        <p role="alert" className="text-sm text-danger">
          {groups.error.message}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted">No groups yet.</p>
      ) : (
        <ul aria-label="Groups" className="divide-y divide-line rounded-md border border-line">
          {rows.map((group) => (
            <li key={group.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <Link
                  to={`/settings/groups/${group.id}/members`}
                  className="text-sm font-medium hover:underline"
                >
                  {group.name}
                </Link>
                {group.description !== null && group.description !== undefined && (
                  <p className="truncate text-xs text-muted">{group.description}</p>
                )}
              </div>
              <span className="text-xs text-muted">
                {`${group.memberCount ?? 0} member${group.memberCount === 1 ? "" : "s"}`}
              </span>
              <Menu
                buttonLabel={`Actions for ${group.name}`}
                menuLabel={`Actions for ${group.name}`}
                items={menuItems(group)}
              />
            </li>
          ))}
        </ul>
      )}

      {draft !== null && (
        <GroupDialog
          draft={draft}
          onClose={() => {
            setDraft(null);
          }}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete this group?"
        description={`Every grant “${pendingDelete?.name ?? ""}” holds on collections and documents goes with it, and anyone whose access came only through this group loses it immediately.`}
        confirmLabel="Delete group"
        destructive
        busy={remove.isPending}
        onClose={() => {
          setPendingDelete(null);
        }}
        onConfirm={() => {
          const target = pendingDelete;
          if (target === null) return;
          remove.mutate(
            { id: target.id },
            {
              onSuccess: () => {
                setPendingDelete(null);
                toast.show(`Group “${target.name}” deleted.`);
              },
              onError: (error) => {
                setPendingDelete(null);
                toast.show(error.message, "error");
              },
            },
          );
        }}
      />
    </SettingsPage>
  );
}
